<!-- eslint-disable vue/multi-word-component-names -->
<!-- components/TinyMCE.vue -->
<template>
  <div class="editor-wrapper">
    <div ref="editorEl" class="tinymce-editor" v-mathml></div>
    <!-- 添加画板弹窗组件 -->
    <DrawingBoard ref="drawingBoard" @save-image="handleSaveImage" />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, defineProps, defineEmits } from "vue";
// import { uploadFileApi } from "@/api/file.js";
import { ElMessage, ElLoadingService } from "element-plus";
import { common } from "@/api/api";
import DrawingBoard from "@/components/quillEditor/drawingBoard.vue"; // 引入画板弹窗组件

const props = defineProps({
  modelValue: String,
});

const emit = defineEmits(["update:modelValue"]);
const editorEl = ref(null); // 编辑器容器 DOM 引用
const drawingBoard = ref(null); // 画板弹窗引用
let editorInstance = null; // TinyMCE 实例

// 初始化编辑器
const initEditor = async () => {
  await window.tinymce
    .init({
      target: editorEl.value, // 绑定 DOM 元素
      skin: "oxide", // 皮肤名称
      content_css: "default", // 内容样式
      language: "zh_CN",
      menubar: false, // 关闭顶部菜单
      contextmenu_never_use_native: true, // 是否屏蔽浏览器本身的右键菜单
      elementpath: false, // 关闭左下角路径选择
      // plugins:
      //   "hr link lists code image media table indent2em wordcount kityformula-editor",
      plugins: "hr paste link lists code image media table indent2em wordcount kityformula-editor",
      toolbar: [
        "undo redo removeformat | bold italic underline strikethrough subscript superscript | fontselect | fontsizeselect | forecolor backcolor | lineheight | alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent indent2em | link unlink image media | hr table kityformula-editor | code | draw", // | draw 画板模块 
      ],
      lineheight_formats: "1.0 1.5 2.0 2.5 3",
      fontsize_formats:
        "12px 14px 16px 18px 20px 22px 24px 28px 32px 36px 48px 56px 72px", // 字体大小
      font_formats:
        "微软雅黑=Microsoft YaHei,Helvetica Neue,PingFang SC,sans-serif;苹果苹方=PingFang SC,Microsoft YaHei,sans-serif;宋体=simsun,serif;仿宋体=FangSong,serif;黑体=SimHei,sans-serif;Arial=arial,helvetica,sans-serif;Arial Black=arial black,avant garde;Book Antiqua=book antiqua,palatino;",
      /** 禁用粘贴图片解析成base64字符的问题 */
      paste_data_images: false,
      /** 媒体处理封装 */
      file_picker_types: "image media", // 允许处理媒体类型
      file_picker_callback: function (callback, value, meta) {
        if (meta.filetype === "media") {
          let input = document.createElement("input");
          input.setAttribute("type", "file");
          input.setAttribute("accept", "video/*"); // 限制为视频文件

          input.onchange = function () {
            const loadingInstance = ElLoadingService({
              lock: true,
              customClass: "zindex9999",
              text: "上传中...",
              background: "rgba(0, 0, 0, 0.7)",
            });

            let file = this.files[0];
            const formData = new FormData();
            formData.append("file", file);
            common
              .uploadFileWithProgress(formData, (progressEvent) => {
                if (progressEvent.lengthComputable) {
                  const percent = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                  );

                  // 更新进度显示
                  this.progress = percent;
                  // 计算上传速度和剩余时间
                  // this.calculateSpeedAndTime(progressEvent.loaded, progressEvent.total);
                }
              })
              .then((res) => {
                callback(res.data.data.url, { alt: res.data.data.name });
                ElMessage.success("上传成功");
              })
              .catch((e) => {
                ElMessage.error("上传失败");
                console.error(e);
              })
              .finally(() => {
                loadingInstance.close();
              });
          };

          input.click();
        } else if (meta.filetype === "image") {
          let input = document.createElement("input");
          input.setAttribute("type", "file");
          input.setAttribute("accept", "image/*"); // 限制为视频文件

          input.onchange = function () {
            const loadingInstance = ElLoadingService({
              lock: true,
              customClass: "zindex9999",
              text: "上传中...",
              background: "rgba(0, 0, 0, 0.7)",
            });

            let file = this.files[0];
            const formData = new FormData();
            formData.append("file", file);
            common
              .uploadFile(formData)
              .then((res) => {
                callback(res.data.data.url, { alt: res.data.data.name });
                ElMessage.success("上传成功");
              })
              .catch((e) => {
                ElMessage.error("上传失败");
                console.error(e);
              })
              .finally(() => {
                loadingInstance.close();
              });
          };

          input.click();
        }
      },

      // 关键配置：允许保留 Base64 图片
      images_dataimg_filter: (img) => {
        console.log(img);
        return false;
      },

      setup: (editor) => {
        console.log(editor);
        // editor.ui.registry.addIcon('draw_icon', 'https://qncc.nssxgh.cn/uploadFile/2025/04/28/0d306c9dde734ac8b2f5923a50648bc5.jpg'); // 添加SVG图标定义
        // 注册一个名为'draw_icon'的图标，使用SVG字符串
        editor.ui.registry.addIcon('draw_icon', '<svg viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M18,4H6A2,2 0 0,0 4,6V18A2,2 0 0,0 6,20H18A2,2 0 0,0 20,18V6A2,2 0 0,0 18,4M18,6V12H6V6H18Z"/></svg>');

        // 添加自定义按钮
        editor.ui.registry.addButton('draw', {
          icon: 'draw_icon',
          tooltip: '打开画板',
          onAction: function () {
            openDrawingDialog(editor);
          }
        });
      },

      init_instance_callback: (editor) => {
        // 同步初始内容
        editor.setContent(props.modelValue || "");

        // 监听内容变化
        editor.on("input change undo redo", () => {
          emit("update:modelValue", editor.getContent());
        });
      },
    })
    .then((editors) => {
      editorInstance = editors[0];
    });
};

// 在初始化函数中添加重试逻辑
let retryCount = 0;
const MAX_RETRY = 3;

const safeInitEditor = async () => {
  try {
    await initEditor();
    retryCount = 0;
  } catch (error) {
    console.error(`编辑器初始化失败 (${retryCount + 1}/${MAX_RETRY}):`, error);
    
    if (retryCount < MAX_RETRY) {
      retryCount++;
      // 等待一段时间后重试
      await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
      await safeInitEditor();
    } else {
      console.error("编辑器初始化失败，已达到最大重试次数");
    }
  }
};

// 组件挂载时初始化
onMounted(async () => {
  // 等待TinyMCE完全加载
  const checkTinyMCE = () => {
    return new Promise((resolve) => {
      const check = () => {
        if (window.tinymce && typeof window.tinymce.init === 'function') {
          resolve();
        } else {
          setTimeout(check, 100);
        }
      };
      check();
    });
  };

  try {
    await checkTinyMCE();
    await safeInitEditor();
  } catch (error) {
    console.error('TinyMCE初始化失败:', error);
    ElMessage.error('编辑器初始化失败，请刷新页面重试');
  }
});

// 组件销毁时释放资源
onBeforeUnmount(() => {
  if (editorInstance) {
    editorInstance.destroy();
    editorInstance = null;
  }
});

// 监听外部内容变化
watch(
  () => props.modelValue,
  (newVal) => {
    if (editorInstance && newVal !== editorInstance.getContent()) {
      editorInstance.setContent(newVal);
    }
  }
);
const openDrawingDialog = (editor) => {
  if (drawingBoard.value) {
    // 保存编辑器实例，用于后续插入图片
    drawingBoard.value.editorInstance = editor;
    // 打开弹窗
    drawingBoard.value.openDrawingBoard();
  }
};
// 处理保存的图片
const handleSaveImage = (imageData) => {
  if (editorInstance && imageData) {
    const loadingInstance = ElLoadingService({
      lock: true,
      customClass: "zindex9999",
      text: "上传中...",
      background: "rgba(0, 0, 0, 0.7)",
    });

    // 通过blob URL获取blob数据并上传
    fetch(imageData)
      .then(res => res.blob())
      .then(blob => {
        // 将blob转换为File对象
        const file = new File([blob], 'drawing.png', { type: 'image/png' });
        const formData = new FormData();
        formData.append('file', file);

        return common.uploadFile(formData);
      })
      .then((res) => {
        const imageUrl = res.data.data.url; // 根据实际接口调整
        // 将图片插入到编辑器
        editorInstance.insertContent(`<img src="${imageUrl}" alt="手绘图片" style="max-width: 100%;"/>`);
        ElMessage.success("图片已插入编辑器");
      })
      .catch((e) => {
        ElMessage.error("上传失败");
        console.error(e);
      })
      .finally(() => {
        loadingInstance.close();
      });
  }
};
</script>

<style lang="scss" scoped>
/* 确保编辑器高度生效 */
.editor-wrapper {
  width: 100%;
}

.tinymce-editor {
  width: 100%;
  min-height: 300px;

  img,
  video {
    max-width: 100%;
    height: auto;
  }
}
</style>
